import apiClient from '../api/api';

export interface MCPTool {
  name: string;
  description: string;
  is_active: boolean;
  inputSchema?: {
    type: string;
    properties: Record<string, any>;
    required: string[];
  };
  server_name?: string;
}

export interface MCPConfig {
  _id: string;
  mcp_name: string;
  mcp_connection_npx_command: string;
  tools?: MCPTool[];
  last_sync_at?: string;
  is_deleted: boolean;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

export interface CreateMCPConfigData {
  mcp_name: string;
  mcp_connection_npx_command: string;
}

export interface UpdateMCPConfigData extends CreateMCPConfigData { }

export interface MCPConfigListParams {
  skip: number;
  limit: number;
  mcp_name?: string;
}

export interface MCPConfigListResponse {
  success: boolean;
  code: number;
  data: {
    total_count: number;
    config: MCPConfig[];
  };
}

export interface MCPConfigResponse {
  success: boolean;
  code: number;
  data: MCPConfig;
}

export interface MCPConfigStats {
  total_configs: number;
  active_configs: number;
  configs_with_tools: number;
}

export interface ActivateToolsData {
  mcp_id: string;
  tools: {
    name: string;
    is_active: boolean;
  }[];
}

class MCPConfigService {
  // GET: Fetch list with pagination and filters
  async getMCPConfigs(params: MCPConfigListParams): Promise<MCPConfigListResponse> {
    try {
      const response = await apiClient.post('/dms-v2-srv/mcp-config-srv/utils/list', params);
      return response.data;
    } catch (error) {
      console.error('Error fetching MCP configs:', error);
      throw error;
    }
  }

  // GET: Fetch single MCP config by ID
  async getMCPConfigById(id: string): Promise<MCPConfig> {
    try {
      const response = await apiClient.get(`/dms-v2-srv/mcp-config-srv/${id}`);
      return response.data.data;
    } catch (error) {
      console.error(`Error fetching MCP config ${id}:`, error);
      throw error;
    }
  }

  // POST: Create new MCP config
  async createMCPConfig(configData: CreateMCPConfigData): Promise<MCPConfig> {
    try {
      const response = await apiClient.post('/dms-v2-srv/mcp-config-srv', configData);
      return response.data.data;
    } catch (error) {
      console.error('Error creating MCP config:', error);
      throw error;
    }
  }

  // PUT: Update existing MCP config
  async updateMCPConfig(id: string, configData: UpdateMCPConfigData): Promise<MCPConfig> {
    try {
      const response = await apiClient.put(`/dms-v2-srv/mcp-config-srv/${id}`, configData);
      return response.data.data;
    } catch (error) {
      console.error(`Error updating MCP config ${id}:`, error);
      throw error;
    }
  }

  // DELETE: Remove MCP config
  async deleteMCPConfig(id: string): Promise<void> {
    try {
      await apiClient.delete(`/dms-v2-srv/mcp-config-srv/${id}`);
    } catch (error) {
      console.error(`Error deleting MCP config ${id}:`, error);
      throw error;
    }
  }

  // PUT: Sync tools for MCP config
  async syncMCPConfigTools(id: string): Promise<MCPConfig> {
    try {
      const response = await apiClient.put(`/dms-v2-srv/mcp-config-srv/${id}/sync-tools`);
      return response.data.data;
    } catch (error) {
      console.error(`Error syncing MCP config tools ${id}:`, error);
      throw error;
    }
  }

  // POST: Activate/deactivate tools for MCP config
  async activateTools(data: ActivateToolsData): Promise<void> {
    try {
      await apiClient.post('/dms-v2-srv/mcp-config-srv/activate-tools', data);
    } catch (error) {
      console.error('Error activating/deactivating tools:', error);
      throw error;
    }
  }

  // GET: Fetch MCP config statistics
  async getMCPConfigStats(): Promise<MCPConfigStats> {
    try {
      // Calculate stats from the list since no dedicated stats endpoint exists
      const response = await this.getMCPConfigs({ skip: 0, limit: 1000 });
      const configs = response.data.config || [];

      // Helper function to check if config has tools
      const hasTools = (config: MCPConfig) => {
        return config.tools && Array.isArray(config.tools) && config.tools.length > 0;
      };

      const stats: MCPConfigStats = {
        total_configs: configs.length,
        active_configs: configs.filter(c => !c.is_deleted).length,
        configs_with_tools: configs.filter(c => hasTools(c)).length,
      };

      return stats;
    } catch (error) {
      console.error('Error fetching MCP config stats:', error);
      // Return default stats on error
      return {
        total_configs: 0,
        active_configs: 0,
        configs_with_tools: 0,
      };
    }
  }
}

export const mcpConfigService = new MCPConfigService();
export default mcpConfigService;